import React, { useState, useRef, useEffect } from 'react';
import './style.css';

const WHATSAPP_NUMBER = process.env.REACT_APP_FERRETI_WHATSAPP || '351900000000';

const SIDEBAR_CHIPS = [
  'Stack principal',
  'Experiência em Portugal',
  'Setores',
  'Soft skills',
  'Idiomas',
  'Disponibilidade'
];

const QUICK_BUTTONS = [
  'Quais stacks domina?',
  'Tem experiência internacional?',
  'Quais setores já atendeu?',
  'Quais soft skills se destacam?'
];

// Renderiza texto simples com suporte a **negrito**, listas numeradas e com marcadores
const renderFormattedText = (text) => {
  if (!text) return null;
  const lines = text.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);

  const renderInline = (line) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return parts.map((part, i) =>
      part.startsWith('**') && part.endsWith('**') ? (
        <strong key={i}>{part.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
  };

  const elements = [];
  let listBuffer = [];
  let listType = null; // 'ul' | 'ol'

  const flushList = () => {
    if (listBuffer.length === 0) return;
    const ListTag = listType === 'ol' ? 'ol' : 'ul';
    elements.push(
      <ListTag key={`list-${elements.length}`} style={{ margin: '6px 0', paddingLeft: '20px' }}>
        {listBuffer.map((item, i) => (
          <li key={i} style={{ marginBottom: '4px' }}>{renderInline(item)}</li>
        ))}
      </ListTag>
    );
    listBuffer = [];
    listType = null;
  };

  lines.forEach((line, idx) => {
    const numberedMatch = line.match(/^(\d+)[.)]\s+(.*)/);
    const bulletMatch = line.match(/^[-•*]\s+(.*)/);

    if (numberedMatch) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listBuffer.push(numberedMatch[2]);
    } else if (bulletMatch) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listBuffer.push(bulletMatch[1]);
    } else {
      flushList();
      elements.push(<p key={`p-${idx}`} style={{ margin: idx === 0 ? 0 : '8px 0 0' }}>{renderInline(line)}</p>);
    }
  });
  flushList();

  return elements;
};

const FerretiCVChat = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Olá! Sou o CV Bot do Leonardo Ferreti. Pode me perguntar sobre experiência, skills técnicas, soft skills, carreira, idiomas ou disponibilidade.',
      timestamp: new Date(),
      source: 'knowledge_base'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [unknownQuestion, setUnknownQuestion] = useState(null);
  const [theme, setTheme] = useState('system');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Base de conhecimento pré-programada
  const knowledgeBase = {
    skills_técnicas: [
      {
        patterns: ['quais linguagens', 'stack técnica', 'tecnologias', 'linguagem de programação', '.net', 'c#', 'csharp'],
        answer: 'Minha stack principal é .NET: C#, .NET Framework, .NET Core, ASP.NET MVC/Core, WebForms, WCF, Entity Framework, Dapper e PetaPoco (DAL2). No frontend trabalho com AngularJS, JavaScript, Bootstrap e HTML5/CSS3.'
      },
      {
        patterns: ['dnn', 'dotnetnuke', 'cms', 'consultor dnn'],
        answer: 'Sou consultor reconhecido no ecossistema DotNetNuke (DNN): desenvolvimento de módulos MVC/WebForms, Skin Objects, PersonaBar, providers SQL e localização multi-cultura.'
      },
      {
        patterns: ['banco de dados', 'sql', 'postgres', 'postgresql', 'mongodb', 'oracle', 'elasticsearch', 'documentdb'],
        answer: 'Tenho forte experiência com SQL Server, PostgreSQL, MongoDB/DocumentDB, Oracle e ElasticSearch — desde queries otimizadas e indexação até modelagem para sistemas de alta disponibilidade.'
      },
      {
        patterns: ['cloud', 'aws', 'azure', 'devops', 'ci/cd', 'deploy', 'lambda', 'ecs', 'octopus'],
        answer: 'Trabalho com AWS (DocumentDB, Lambda, ECS) e Azure DevOps, incluindo pipelines de CI/CD com Octopus Deploy e Git Flow. Tenho experiência real migrando sistemas legados para arquiteturas cloud-ready.'
      },
      {
        patterns: ['integração', 'rabbitmq', 'mensageria', 'k2', 'workflow', 'rest api', 'sistemas distribuídos'],
        answer: 'Domino integração de sistemas empresariais via RabbitMQ (mensageria assíncrona), workflows K2 e REST APIs, com foco em arquiteturas distribuídas e desacoplamento entre componentes.'
      },
      {
        patterns: ['práticas', 'tdd', 'solid', 'clean code', 'qualidade de código', 'boas práticas'],
        answer: 'Sigo TDD, princípios SOLID e Clean Code como padrão de trabalho, com foco em código testável, performance e escalabilidade — e isso também orienta minhas revisões de código e mentoria de equipas.'
      }
    ],
    experiência: [
      {
        patterns: ['experiência em portugal', 'experiencia em portugal', 'projetos em portugal', 'trajetória em portugal', 'carreira em portugal'],
        answer: 'Vim para Portugal em 2019, num momento que considero o maior desafio da minha carreira: mudar de país e, ao mesmo tempo, lidar com tecnologias novas para mim, correndo atrás de documentação para conseguir entregar com qualidade. De lá pra cá, em ordem cronológica, minha trajetória por aqui foi:\n- **ADENTIS** (Lisboa, setor de Seguros, jun-dez/2019): minha porta de entrada no mercado português — desenvolvi um sistema full-stack greenfield em .NET, da arquitetura à entrega, aplicando TDD e SOLID, com DotNetNuke, Oracle e SQL Server\n- **Integer Consulting** (Lisboa, jan/2020-fev/2021): atuei no backend de sistemas empresariais, com foco em otimização de performance de dados usando SQL Server e Dapper\n- **Viagens Abreu** (Oeiras, fev/2021-dez/2025): quase 5 anos como Senior Software Developer, desenvolvendo e modernizando sistemas de viagens de larga escala — integração via RabbitMQ, otimização de performance e pipelines de CI/CD com Azure DevOps e Octopus Deploy\n- **Wrightia** (projeto paralelo em 2025): atualização dos portais, sites e backoffice da Justiça de Portugal, desenvolvidos em DotNetNuke (DNN)\n- **Jobbex IT** (Lisboa, setor de Energia, desde dez/2025): minha posição atual como Tech Lead, onde tomo decisões de arquitetura backend em .NET, dou suporte a uma plataforma de pagamentos sobre AWS (tokenização de cartões, K2, RabbitMQ, MongoDB/DocumentDB, PostgreSQL), faço diagnóstico de falhas em produção e mentoria de equipas\n\nNesses quase 7 anos no país, fui evoluindo de desenvolvedor pleno para uma posição de liderança técnica, sempre mantendo contato com o ecossistema DotNetNuke, que acompanha minha carreira desde o início por aqui.'
      },
      {
        patterns: ['experiência', 'trabalhos anteriores', 'empresas', 'histórico profissional', 'quantos anos de experiencia', 'anos de experiencia'],
        answer: 'Tenho mais de 15 anos de experiência em desenho, modernização e escalabilidade de sistemas empresariais de missão crítica, com passagens por setores de energia, viagens, seguros e financeiro, no Brasil e em Portugal.'
      },
      {
        patterns: ['cargo atual', 'onde trabalha', 'emprego atual', 'tech lead', 'jobbex'],
        answer: 'Atualmente sou Tech Lead na Jobbex IT (setor de Energia, Lisboa), liderando decisões de arquitetura backend em .NET, dando suporte a uma plataforma de pagamentos sobre AWS (com K2, RabbitMQ, MongoDB/DocumentDB e PostgreSQL), além de mentoria técnica e modernização de sistemas legados.'
      },
      {
        patterns: ['maior projeto', 'principal projeto', 'orgulho', 'gateway de pagamentos', 'pagamentos'],
        answer: 'Um dos projetos de maior destaque é o suporte à plataforma de pagamentos da Jobbex sobre AWS — processamento de transações, tokenização de cartões e orquestração de fluxos de pagamento integrando K2, RabbitMQ, MongoDB/DocumentDB e PostgreSQL, incluindo diagnóstico de falhas críticas em produção (Lambda/ECS).'
      },
      {
        patterns: ['viagens abreu', 'abreu', 'senior software developer'],
        answer: 'Na Viagens Abreu (2021–2025) atuei como Senior Software Developer, desenvolvendo e modernizando sistemas empresariais de viagens de larga escala, com integração via RabbitMQ, otimização de performance e pipelines CI/CD em Azure DevOps e Octopus Deploy.'
      },
      {
        patterns: ['greenfield', 'do zero', 'projeto desde o início', 'adentis'],
        answer: 'Na ADENTIS (setor de Seguros) desenvolvi de raiz (greenfield) um sistema empresarial full-stack em .NET, da arquitetura à entrega, aplicando TDD e SOLID, com DotNetNuke e integração com Oracle e SQL Server.'
      }
    ],
    formação: [
      {
        patterns: ['formação', 'graduação', 'faculdade', 'estudou', 'educação', 'pós-graduação'],
        answer: 'Tenho formação em Análise e Desenvolvimento de Sistemas (Descomplica, 2022–2024), Pós-graduação em Desenvolvimento Java (Estácio, 2015–2017) e Bacharelado em Administração (UNIABEU, 2006–2010).'
      },
      {
        patterns: ['certificação', 'certificado', 'curso'],
        answer: 'Tenho certificações em ASP.NET MVC 5, DotNetNuke (DNN), ASP.NET MVC Core, Firebase e Bootcamp Full Stack — sempre me atualizando com cursos e projetos práticos.'
      },
      {
        patterns: ['idioma', 'línguas', 'inglês', 'espanhol', 'português'],
        answer: 'Falo Português (nativo), Inglês (nível B2 — bom para o dia a dia profissional) e Espanhol (nível A1 — noções básicas).'
      }
    ],
    soft_skills: [
      {
        patterns: ['trabalho em equipe', 'liderança', 'comunicação', 'soft skills', 'mentoria', 'mentor'],
        answer: 'Acredito que técnica sem comunicação não chega longe — por isso invisto bastante nessas competências:\n- **Liderança técnica**: como Tech Lead, oriento decisões de arquitetura e assumo a responsabilidade pelas escolhas do time\n- **Mentoria**: acompanho de perto o crescimento de devs mais juniores, com revisões de código que explicam o "porquê", não só o "o quê"\n- **Comunicação clara**: traduzo problemas técnicos complexos em termos que stakeholders não-técnicos conseguem entender e decidir\n- **Adaptabilidade**: minha mudança para Portugal me obrigou a me adaptar rápido a novas equipas, culturas e tecnologias — algo que carrego comigo até hoje\n- **Melhoria contínua**: gosto de questionar o "sempre foi assim" e propor formas melhores de trabalhar, tanto no código quanto nos processos do time'
      },
      {
        patterns: ['maior desafio', 'desafio profissional', 'desafio na carreira'],
        answer: 'Meu maior desafio profissional até agora foi na ADENTIS: mudei de país e precisei trabalhar com tecnologias com as quais ainda não tinha experiência, correndo atrás de documentação e aprendendo rapidamente para entregar um projeto greenfield do zero.'
      },
      {
        patterns: ['resolução de problemas', 'learning', 'aprendizado', 'diagnóstico', 'investigação de falhas'],
        answer: 'Tenho experiência sólida em diagnóstico e resolução de falhas críticas em produção — por exemplo, investigação de falhas de autenticação em Lambda/ECS por credenciais inválidas de DocumentDB — sempre buscando a causa raiz e a melhoria do sistema como um todo.'
      },
      {
        patterns: ['modernização', 'sistemas legados', 'legado', 'migração'],
        answer: 'Tenho forte experiência em modernização de sistemas legados — incluindo o descomissionamento de plataformas como TIBCO e a migração para arquiteturas cloud-ready, reduzindo dívida técnica e melhorando a manutenibilidade.'
      }
    ],
    setores: [
      {
        patterns: ['quais setores', 'setores que ja atendeu', 'setores já atendeu', 'setores de atuação', 'mercados que atuou', 'industrias que trabalhou', 'indústrias que trabalhou'],
        answer: 'Passei por setores bem diferentes entre si ao longo da carreira, o que me deu uma visão ampla de como sistemas críticos funcionam em cada contexto. Em ordem cronológica:\n- **Gestão/ERP**: comecei na SmartSis e depois na GigaNet, no Rio de Janeiro, desenvolvendo sistemas de ERP, relatórios (Crystal Reports) e gestão para diferentes clientes\n- **Financeiro**: na BRQ, também no Rio, atuei como Analista de Sistemas Pleno em projetos do setor financeiro\n- **Seguros**: já em Portugal, na ADENTIS, desenvolvi um sistema greenfield em .NET do zero, da arquitetura à entrega\n- **Backend empresarial**: na Integer Consulting fiz manutenção do sistema interno da empresa, com foco em performance de dados usando SQL Server e Dapper\n- **Viagens/turismo**: quase 5 anos na Viagens Abreu, modernizando sistemas empresariais de larga escala da indústria de viagens\n- **Setor público/justiça**: em projeto paralelo pela Wrightia, atualizei os portais e o backoffice da Justiça de Portugal\n- **Energia** (atual): sou Tech Lead na Jobbex IT, atuando no projeto da Galp — não só na plataforma de pagamentos, mas de forma mais ampla na arquitetura backend, modernização de sistemas legados e diagnóstico de falhas em produção\n\nEssa diversidade setorial me ajuda a entender rápido o contexto de negócio de cada empresa em que entro.'
      }
    ],
    pessoal: [
      {
        patterns: ['disponibilidade', 'disponivel para comecar', 'disponível para começar', 'quando pode comecar', 'quando pode começar', 'aviso prévio', 'aviso previo', 'prazo para comecar', 'prazo para começar'],
        answer: 'Atualmente estou empregado, então minha disponibilidade depende do período de aviso prévio — de forma geral, consigo me organizar para iniciar em algo entre 15 e 30 dias.'
      },
      {
        patterns: ['vive em portugal há quanto tempo', 'há quanto tempo', 'desde quando mora', 'mudou para portugal', 'tempo em portugal'],
        answer: 'Vivo em Portugal desde 2019.'
      },
      {
        patterns: ['localização', 'onde mora', 'city', 'país', 'cidade'],
        answer: 'Estou baseado em Alcanena / Santarém, Portugal. Atualmente atuo em Lisboa (Jobbex IT) e tenho experiência também trabalhando remoto.'
      },
      {
        patterns: ['contato', 'email', 'linkedin', 'como falar'],
        answer: 'Pode me contatar por e-mail (leo.ferreti@gmail.com) ou pelo LinkedIn (linkedin.com/in/leonardoferreti).'
      }
    ]
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Remove acentos para tornar a busca por patterns mais tolerante (ex: "ha" === "há")
  const normalize = (str) =>
    str.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  const findAnswer = (userQuestion) => {
    const questionNormalized = normalize(userQuestion);

    for (const category in knowledgeBase) {
      for (const qa of knowledgeBase[category]) {
        if (qa.patterns.some(pattern => questionNormalized.includes(normalize(pattern)))) {
          return qa.answer;
        }
      }
    }
    return null;
  };

  const askLocalAI = async (question) => {
    try {
      const response = await fetch('/api/ai/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question })
      });
      if (!response.ok) {
        console.warn(`IA local indisponível (status ${response.status}):`, await response.text());
        return null;
      }
      const data = await response.json();
      return data.answer || null;
    } catch (error) {
      console.error('Erro ao consultar IA local:', error);
      return null;
    }
  };

  const logUnansweredQuestion = (question) => {
    fetch('/api/analytics/question', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, found: false, timestamp: new Date().toISOString() })
    }).catch((error) => console.error('Erro ao registar pergunta sem resposta:', error));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Adiciona mensagem do usuário
    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: input,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    // Aguarda 500ms para simular processamento
    setTimeout(async () => {
      const answer = findAnswer(input);
      
      if (answer) {
        // Resposta encontrada na base de conhecimento
        const botMessage = {
          id: messages.length + 2,
          type: 'bot',
          text: answer,
          timestamp: new Date(),
          source: 'knowledge_base'
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        // Não está na base de conhecimento - tenta a IA local (Ollama) antes do fallback WhatsApp
        const aiAnswer = await askLocalAI(input);

        if (aiAnswer && aiAnswer.includes('[REDIRECT_WHATSAPP]')) {
          const botMessage = {
            id: messages.length + 2,
            type: 'bot',
            text: "Essa pergunta foge um pouco do escopo do meu CV — para não arriscar te dar uma resposta imprecisa, prefiro falar diretamente com você. Pode me chamar pelo WhatsApp clicando no botão abaixo, ou por email para leo.ferreti@gmail.com.",
            timestamp: new Date(),
            source: 'not_found',
            question: input
          };
          setMessages(prev => [...prev, botMessage]);
          setUnknownQuestion(input);
          logUnansweredQuestion(input);
        } else if (aiAnswer) {
          const botMessage = {
            id: messages.length + 2,
            type: 'bot',
            text: aiAnswer,
            timestamp: new Date(),
            source: 'ai'
          };
          setMessages(prev => [...prev, botMessage]);
        } else {
          // IA local indisponível - oferecer contato direto
          const botMessage = {
            id: messages.length + 2,
            type: 'bot',
            text: "❓ Essa pergunta ainda não está catalogada e a IA local está indisponível no momento. Envie diretamente pelo WhatsApp clicando no botão abaixo, ou por email para leo.ferreti@gmail.com.",
            timestamp: new Date(),
            source: 'not_found',
            question: input
          };
          setMessages(prev => [...prev, botMessage]);
          setUnknownQuestion(input);
          logUnansweredQuestion(input);
        }
      }
      setLoading(false);
    }, 500);
  };

  const sourceLabel = (source) => {
    switch (source) {
      case 'knowledge_base': return 'Base de conhecimento';
      case 'ai': return 'Resposta gerada por IA local';
      case 'not_found': return 'Mensagem para Leonardo Ferreti';
      default: return null;
    }
  };

  const fillInput = (text) => {
    setInput(text);
  };

  return (
    <main className="layout">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-icon">Ferreti</div>
          <div>
            <h1>Ferreti CV Chat</h1>
            <p>Pergunte, eu respondo — sou o Leonardo (assistido por IA)</p>
          </div>
        </div>

        <div className="sidebar-block">
          <div className="sidebar-title">Atalhos</div>
          <div className="chip-list">
            {SIDEBAR_CHIPS.map((chip) => (
              <button key={chip} className="chip" type="button" onClick={() => fillInput(chip)}>
                {chip}
              </button>
            ))}
          </div>
        </div>

        <div className="sidebar-block">
          <div className="sidebar-title">Resumo</div>
          <div className="profile-card">
            <strong>Perfil profissional</strong>
            <span>
              Tech Lead com 15+ anos em sistemas corporativos .NET, modernização de aplicações,
              escalabilidade e atuação em contextos de alta criticidade (Brasil e Portugal).
            </span>
          </div>
        </div>
      </aside>

      <section className="main">
        <section className="chat-shell">
          <header className="topbar">
            <div>
              <h2>Bate-papo direto comigo, o Leonardo</h2>
              <p>Sem formulários. Pergunte o que quiser sobre minha trajetória, projetos, stack ou mudança para Portugal — eu respondo.</p>
            </div>

            <div className="topbar-right">
              <div className="status">
                <span className="status-dot"></span>
                Disponível
              </div>

              <select
                className="theme-control"
                aria-label="Selecionar tema"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
              >
                <option value="system">Seguir padrão do dispositivo</option>
                <option value="light">White</option>
                <option value="dark">Dark</option>
              </select>
            </div>
          </header>

          <div className="hero">
            <div className="hero-badge">Olá, eu sou o Leonardo 👋</div>
            <h3>Curioso sobre minha carreira? Pergunte direto pra mim.</h3>
            <p>
              Tech Lead .NET há mais de 15 anos, brasileiro vivendo em Portugal desde 2019.
              Pergunte sobre minha trajetória, os desafios que já enfrentei, a stack que domino
              ou como seria trabalhar comigo — vou responder com a maior transparência possível.
            </p>
            <div className="quick-buttons">
              {QUICK_BUTTONS.map((q) => (
                <button key={q} className="quick-btn" type="button" onClick={() => fillInput(q)}>
                  {q}
                </button>
              ))}
            </div>
          </div>

          <div className="chat-body">
            <div className="day-divider"><span>Hoje</span></div>

            {messages.map((msg) => {
              const label = sourceLabel(msg.source);
              const isWarning = msg.source === 'not_found';
              return (
                <div
                  key={msg.id}
                  className={`row ${msg.type === 'user' ? 'user' : 'bot'}${isWarning ? ' warning' : ''}`}
                >
                  {msg.type === 'bot' && (
                    <div className="avatar">{isWarning ? '!' : msg.source === 'ai' ? '🧠' : '🤖'}</div>
                  )}
                  <div className="message">
                    <div className="bubble">
                      {msg.type === 'bot' ? renderFormattedText(msg.text) : msg.text}
                      {isWarning && (
                        <div className="inline-actions">
                          <a
                            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                              `Olá Leonardo Ferreti! Vi seu CV interativo e tenho uma pergunta: "${msg.question}"`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-btn primary"
                          >
                            Enviar pelo WhatsApp
                          </a>
                          <a href="mailto:leo.ferreti@gmail.com" className="inline-btn secondary">
                            Enviar email
                          </a>
                        </div>
                      )}
                    </div>
                    {label && <div className="meta">{label}</div>}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div className="row bot">
                <div className="avatar">🤖</div>
                <div className="message">
                  <div className="bubble">
                    <div className="typing-dots">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </section>

        <form className="composer" onSubmit={handleSendMessage}>
          <div className="composer-input">
            <span>⌘</span>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pergunte sobre experiência, skills, carreira ou setores."
              disabled={loading}
            />
          </div>
          <button type="submit" className="send-btn" disabled={loading || !input.trim()}>
            Enviar
          </button>
        </form>
      </section>
    </main>
  );
};

export default FerretiCVChat;
